"use client";

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { usePathname } from "next/navigation";
import { useAtomValue } from "jotai";
import { X } from "lucide-react";
import { Button } from "@/components/shadcn/button";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/components/shadcn/popover";
import { showSpotlight } from "@/lib/genie/spotlight";
import { genieSpotlightAtom } from "@/store/genie-atoms";

const RING_PAD = 6;

// The ring Genie draws around the one element it points the user at, with its
// note beside it. It never blocks the page: the user's next click or keystroke
// anywhere dismisses it, the click on the element and the next message to
// Genie included.
export function GenieSpotlight() {
  const spotlight = useAtomValue(genieSpotlightAtom);
  const pathname = usePathname();
  const [rect, setRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    if (!spotlight) return;
    let frame = 0;
    // Follows the element through scrolling and layout shifts, and goes away
    // when it does.
    const track = () => {
      const box = spotlight.target.getBoundingClientRect();
      // Detached, or hidden in a closed panel or tab.
      if (
        !spotlight.target.isConnected ||
        (box.width === 0 && box.height === 0)
      ) {
        showSpotlight(null);
        return;
      }
      setRect(box);
      frame = window.requestAnimationFrame(track);
    };
    track();
    const dismiss = () => showSpotlight(null);
    document.addEventListener("pointerdown", dismiss, true);
    document.addEventListener("keydown", dismiss, true);
    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener("pointerdown", dismiss, true);
      document.removeEventListener("keydown", dismiss, true);
      setRect(null);
    };
  }, [spotlight]);

  // Made on another page: the user has moved on.
  if (!spotlight || !rect || spotlight.pathname !== pathname) return null;

  return (
    <>
      {createPortal(
        <div
          data-genie-spotlight
          className="pointer-events-none fixed z-50 rounded-lg animate-[glow-pulse_3s_ease-in-out_infinite]"
          style={{
            left: rect.left - RING_PAD,
            top: rect.top - RING_PAD,
            width: rect.width + RING_PAD * 2,
            height: rect.height + RING_PAD * 2,
          }}
        >
          <div className="tech-ring size-full rounded-lg" />
        </div>,
        document.body
      )}
      <Popover open onOpenChange={(open) => !open && showSpotlight(null)}>
        <PopoverAnchor virtualRef={{ current: spotlight.target }} />
        <PopoverContent
          data-genie-spotlight
          align="start"
          sideOffset={RING_PAD + 8}
          collisionPadding={8}
          onOpenAutoFocus={(e) => e.preventDefault()}
          className="flex w-auto max-w-xs items-start gap-2 p-3"
        >
          <p className="flex-1 text-sm">{spotlight.note}</p>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Dismiss"
            className="size-6 shrink-0"
            onClick={() => showSpotlight(null)}
          >
            <X className="size-3" />
          </Button>
        </PopoverContent>
      </Popover>
    </>
  );
}
